import { useId, useState } from "react";

import { useTranslation } from "next-i18next";

import { cn } from "@/common/utils/css.utils";
import { ListingMarkdown } from "@/components/search/plp-seo/ListingMarkdown";
import { PLP_SEO_KEYS } from "@/components/search/plp-seo/plp-seo.i18n";

/**
 * The intro copy, a few lines visible with the rest behind "Read more" (WHO-5534).
 *
 * Collapsing is purely visual: the full text is always in the DOM, so it keeps counting for
 * crawlers and for find-in-page. Collapsed by default on every viewport; three lines of body
 * text show (72px desktop, 60px mobile), which is the two to three sentences the review asked
 * for. The fade hints that there is more.
 */
export const CollapsibleIntro = ({ markdown }: { markdown: string }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();

  return (
    <div>
      <div
        id={contentId}
        className={cn(
          "relative overflow-hidden",
          !expanded &&
            "max-h-[3.75rem] after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-6 after:bg-gradient-to-t after:from-white after:to-transparent md:max-h-[4.5rem]"
        )}
      >
        <ListingMarkdown markdown={markdown} />
      </div>
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={() => setExpanded(value => !value)}
        className="inline-flex min-h-11 items-center font-body text-base text-brand-500 underline"
      >
        {expanded
          ? t(PLP_SEO_KEYS.showLess, "Show less")
          : t(PLP_SEO_KEYS.readMore, "Read more")}
      </button>
    </div>
  );
};
